import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  ChevronLeft, ChevronRight, Pencil, Calculator, AlertTriangle, Check, RefreshCw, Download,
} from "lucide-react";
import {
  MONTH_NAMES, STATUS_COLORS, getDaysInMonth, calculateHours, formatCurrency, API_URL,
} from "../lib/utils";


export default function GhostPage() {
  const { companies, locations, fetchCompanies, fetchLocations } = useApp();

  // Shared state
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [activeTab, setActiveTab] = useState("schedules");

  // Ghost employees
  const [ghostEmployees, setGhostEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Schedule state
  const [schedules, setSchedules] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editForm, setEditForm] = useState({ status: "lavoro", start_time: "08:00", end_time: "16:00" });
  const [saving, setSaving] = useState(false);

  // Payroll state
  const [payrolls, setPayrolls] = useState({});
  const [generating, setGenerating] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [payrollForm, setPayrollForm] = useState({});

  useEffect(() => {
    fetchCompanies();
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompanies, fetchLocations]);

  useEffect(() => {
    if (filterLocation) {
      loadGhostData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, filterLocation]);

  const loadGhostData = async () => {
    if (!filterLocation) return;
    setLoading(true);
    try {
      const empRes = await axios.get(`${API_URL}/api/employees`, {
        params: { location_id: filterLocation },
        withCredentials: true,
      });
      const ghosts = empRes.data.filter(e => e.is_ghost);
      setGhostEmployees(ghosts);

      const ghostIds = ghosts.map(e => e.id);

      // Schedules
      const schedRes = await axios.get(`${API_URL}/api/schedules`, {
        params: { year, month, location_id: filterLocation },
        withCredentials: true,
      });
      const schedMap = {};
      schedRes.data.forEach(s => {
        if (ghostIds.includes(s.employee_id)) schedMap[s.employee_id] = s;
      });
      setSchedules(schedMap);

      // Payrolls
      const payRes = await axios.get(`${API_URL}/api/payrolls`, {
        params: { year, month, location_id: filterLocation },
        withCredentials: true,
      });
      const payMap = {};
      payRes.data.forEach(p => {
        if (ghostIds.includes(p.employee_id)) payMap[p.employee_id] = p;
      });
      setPayrolls(payMap);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else { setMonth(month - 1); }
  };
  const handleNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else { setMonth(month + 1); }
  };

  const filteredLocations = filterCompany
    ? locations.filter(l => l.company_id === filterCompany)
    : locations;

  // ========== SCHEDULE LOGIC ==========
  const daysInMonth = getDaysInMonth(year, month);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const handleCellClick = (employeeId, day) => {
    const schedule = schedules[employeeId];
    const dayData = schedule?.days?.[day] || { status: "", start_time: "", end_time: "" };
    setEditForm({
      status: dayData.status || "lavoro",
      start_time: dayData.start_time || "08:00",
      end_time: dayData.end_time || "16:00",
    });
    setEditingCell({ employeeId, day });
  };

  const handleSaveCell = async () => {
    if (!editingCell) return;
    setSaving(true);
    const { employeeId, day } = editingCell;
    const hours = editForm.status === "lavoro" ? calculateHours(editForm.start_time, editForm.end_time) : 0;
    const currentSchedule = schedules[employeeId] || { days: {} };
    const updatedDays = {
      ...currentSchedule.days,
      [day]: {
        status: editForm.status,
        start_time: editForm.status === "lavoro" ? editForm.start_time : null,
        end_time: editForm.status === "lavoro" ? editForm.end_time : null,
        hours,
      },
    };
    try {
      const response = await axios.post(`${API_URL}/api/schedules`, {
        employee_id: employeeId, year, month, days: updatedDays,
      }, { withCredentials: true });
      setSchedules(prev => ({ ...prev, [employeeId]: response.data }));
      setEditingCell(null);
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getCellContent = (employeeId, day) => {
    const schedule = schedules[employeeId];
    const dayData = schedule?.days?.[day.toString()];
    if (!dayData || !dayData.status) return null;
    const color = STATUS_COLORS[dayData.status];
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-[10px] leading-tight font-ibm-plex-mono"
        style={{ backgroundColor: color?.bg, color: color?.text }}
        title={dayData.status === "lavoro" && dayData.start_time ? `${dayData.start_time}-${dayData.end_time}` : color?.label}
      >
        {dayData.status === "lavoro" ? (
          <>
            <span className="font-semibold">{dayData.hours || 0}h</span>
            {dayData.start_time && dayData.end_time && (
              <>
                <span className="opacity-80 text-[8px]">{dayData.start_time}</span>
                <span className="opacity-80 text-[8px]">{dayData.end_time}</span>
              </>
            )}
          </>
        ) : (
          <span className="text-[9px] font-medium">{color?.cellLabel || color?.label}</span>
        )}
      </div>
    );
  };

  const getTotalHours = (employeeId) => schedules[employeeId]?.total_hours || 0;

  // ========== PAYROLL LOGIC ==========
  const handleGeneratePayrolls = async () => {
    if (!filterLocation) return;
    setGenerating(true);
    try {
      await axios.get(`${API_URL}/api/payrolls/generate/${year}/${month}`, {
        params: { location_id: filterLocation },
        withCredentials: true,
      });
      loadGhostData();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleEditPayroll = (employeeId) => {
    const payroll = payrolls[employeeId];
    const employee = ghostEmployees.find(e => e.id === employeeId);
    setPayrollForm({
      net_pay: payroll?.net_pay?.toString() || employee?.base_salary?.toString() || "0",
      holiday_days_to_deduct: payroll?.holiday_days_to_deduct?.toString() || "0",
      rol_hours_to_deduct: payroll?.rol_hours_to_deduct?.toString() || "0",
      sick_days: payroll?.sick_days?.toString() || "0",
      km_reimbursement: payroll?.km_reimbursement?.toString() || "0",
      expense_reimbursement: payroll?.expense_reimbursement?.toString() || "0",
      advances_paid: payroll?.advances_paid?.toString() || "0",
      is_paid: payroll?.is_paid || false,
      notes: payroll?.notes || "",
    });
    setEditingPayroll(employeeId);
  };

  const handleSavePayroll = async () => {
    if (!editingPayroll) return;
    const netPay = parseFloat(payrollForm.net_pay) || 0;
    const km = parseFloat(payrollForm.km_reimbursement) || 0;
    const expense = parseFloat(payrollForm.expense_reimbursement) || 0;
    const advances = parseFloat(payrollForm.advances_paid) || 0;
    const total = netPay + km + expense - advances;
    const data = {
      employee_id: editingPayroll,
      year, month,
      net_pay: netPay,
      holiday_days_to_deduct: parseFloat(payrollForm.holiday_days_to_deduct) || 0,
      rol_hours_to_deduct: parseFloat(payrollForm.rol_hours_to_deduct) || 0,
      sick_days: parseFloat(payrollForm.sick_days) || 0,
      km_reimbursement: km,
      expense_reimbursement: expense,
      advances_paid: advances,
      is_paid: payrollForm.is_paid,
      total,
      notes: payrollForm.notes,
    };
    try {
      const res = await axios.post(`${API_URL}/api/payrolls`, data, { withCredentials: true });
      setPayrolls(prev => ({ ...prev, [editingPayroll]: res.data }));
      setEditingPayroll(null);
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const isAfterDay10 = new Date().getDate() >= 10;

  const handleExportSchedulePDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/schedule/${year}/${month}`, {
        params: { location_id: filterLocation, ghost_only: true },
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `orario_ghost_${year}_${month.toString().padStart(2, "0")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Errore nell'export: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleExportPayrollPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/payroll/${year}/${month}`, {
        params: { location_id: filterLocation, ghost_only: true },
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `stipendi_ghost_${year}_${month.toString().padStart(2, "0")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Errore nell'export: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="ghost-page">
      <div>
        <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">
          Indennità
        </h1>
        <p className="text-[#52525B] font-ibm-plex mt-1">
          Orari e stipendi dei dipendenti in indennità
        </p>
      </div>

      {/* Filters */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <Select value={filterCompany || "all"} onValueChange={(v) => { setFilterCompany(v === "all" ? "" : v); setFilterLocation(""); }}>
              <SelectTrigger data-testid="ghost-filter-company" className="font-ibm-plex">
                <SelectValue placeholder="Seleziona azienda" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le aziende</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterLocation || "none"} onValueChange={(v) => setFilterLocation(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="ghost-filter-location" className="font-ibm-plex">
                <SelectValue placeholder="Seleziona sede" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleziona sede...</SelectItem>
                {filteredLocations.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevMonth} data-testid="ghost-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 text-center font-ibm-plex font-medium">
                {MONTH_NAMES[month]} {year}
              </div>
              <Button variant="outline" size="icon" onClick={handleNextMonth} data-testid="ghost-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {!filterLocation ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">
            Seleziona una sede per visualizzare i dati Ghost
          </CardContent>
        </Card>
      ) : loading ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">
            Caricamento...
          </CardContent>
        </Card>
      ) : ghostEmployees.length === 0 ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">
            Nessun dipendente Ghost in questa sede
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="schedules" className="font-ibm-plex" data-testid="ghost-tab-schedules">
              Orari
            </TabsTrigger>
            <TabsTrigger value="payrolls" className="font-ibm-plex" data-testid="ghost-tab-payrolls">
              Stipendi
            </TabsTrigger>
          </TabsList>

          {/* ===== SCHEDULES TAB ===== */}
          <TabsContent value="schedules">
            <Card className="border border-[#E4E4E7]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs font-ibm-plex">
                    {Object.entries(STATUS_COLORS).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: val.bg }} />
                        <span>{val.label}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportSchedulePDF}
                    className="font-ibm-plex shrink-0"
                    data-testid="ghost-export-schedule-pdf"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Esporta PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#F4F4F5]">
                        <th className="sticky left-0 bg-[#F4F4F5] z-10 px-3 py-2 text-left font-ibm-plex font-semibold border-r border-b border-[#E4E4E7] min-w-[180px]">
                          Dipendente
                        </th>
                        {days.map(day => (
                          <th key={day} className="px-1 py-2 text-center font-ibm-plex font-semibold border-r border-b border-[#E4E4E7] min-w-[40px]">
                            {day}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-center font-ibm-plex font-semibold border-b border-[#E4E4E7] min-w-[60px]">
                          Ore
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ghostEmployees.map(emp => (
                        <tr key={emp.id} className="hover:bg-[#F4F4F5]/50" data-testid={`ghost-schedule-row-${emp.id}`}>
                          <td className="sticky left-0 bg-white z-10 px-3 py-1 font-ibm-plex font-medium border-r border-b border-[#E4E4E7]">
                            {emp.last_name} {emp.first_name}
                          </td>
                          {days.map(day => (
                            <td key={day} className="border-r border-b border-[#E4E4E7] p-0 cursor-pointer hover:bg-[#E4E4E7]">
                              <Popover
                                open={editingCell?.employeeId === emp.id && editingCell?.day === day.toString()}
                                onOpenChange={(open) => {
                                  if (open) handleCellClick(emp.id, day.toString());
                                  else setEditingCell(null);
                                }}
                              >
                                <PopoverTrigger asChild>
                                  <div className="w-full h-10">
                                    {getCellContent(emp.id, day)}
                                  </div>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-3" align="start">
                                  <div className="space-y-3">
                                    <div className="space-y-1">
                                      <label className="text-xs font-ibm-plex font-medium">Stato</label>
                                      <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                                        <SelectTrigger className="font-ibm-plex h-8"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="lavoro">Lavoro</SelectItem>
                                          <SelectItem value="riposo">Riposo</SelectItem>
                                          <SelectItem value="ferie">Ferie</SelectItem>
                                          <SelectItem value="malattia">Malattia</SelectItem>
                                          <SelectItem value="104">104</SelectItem>
                                          <SelectItem value="permesso_lutto">Permesso Lutto</SelectItem>
                                          <SelectItem value="permesso_matrimoniale">Permesso Matrimoniale</SelectItem>
                                          <SelectItem value="rol">ROL</SelectItem>
                                          <SelectItem value="congedo_parentale">Congedo Parentale</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {editForm.status === "lavoro" && (
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                          <label className="text-xs font-ibm-plex font-medium">Inizio</label>
                                          <Input type="time" value={editForm.start_time} onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })} className="font-ibm-plex h-8" />
                                        </div>
                                        <div className="space-y-1">
                                          <label className="text-xs font-ibm-plex font-medium">Fine</label>
                                          <Input type="time" value={editForm.end_time} onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })} className="font-ibm-plex h-8" />
                                        </div>
                                      </div>
                                    )}
                                    <Button onClick={handleSaveCell} disabled={saving} className="w-full bg-black hover:bg-gray-800 text-white font-ibm-plex h-8" data-testid="ghost-save-schedule-cell">
                                      {saving ? "Salvo..." : "Salva"}
                                    </Button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </td>
                          ))}
                          <td className="px-3 py-1 text-center font-ibm-plex-mono font-medium border-b border-[#E4E4E7]">
                            {getTotalHours(emp.id)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== PAYROLLS TAB ===== */}
          <TabsContent value="payrolls">
            <Card className="border border-[#E4E4E7]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-outfit text-lg">Stipendi Ghost</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportPayrollPDF}
                      className="font-ibm-plex"
                      data-testid="ghost-export-payroll-pdf"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Esporta PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGeneratePayrolls}
                      disabled={generating}
                      className="font-ibm-plex"
                      data-testid="ghost-generate-payrolls"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
                      {generating ? "Generazione..." : "Genera Stipendi"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F4F4F5]">
                        <TableHead className="font-ibm-plex font-semibold">Dipendente</TableHead>
                        <TableHead className="font-ibm-plex font-semibold text-right">Paga Netta</TableHead>
                        <TableHead className="font-ibm-plex font-semibold text-right">Acconti</TableHead>
                        <TableHead className="font-ibm-plex font-semibold text-right">Totale</TableHead>
                        <TableHead className="font-ibm-plex font-semibold text-center">Pagato</TableHead>
                        <TableHead className="font-ibm-plex font-semibold">Note</TableHead>
                        <TableHead className="font-ibm-plex font-semibold w-[60px]">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ghostEmployees.map(emp => {
                        const payroll = payrolls[emp.id];
                        const isPaid = payroll?.is_paid;
                        const showAlert = isAfterDay10 && payroll && !isPaid;
                        return (
                          <TableRow key={emp.id} data-testid={`ghost-payroll-row-${emp.id}`}>
                            <TableCell className="font-ibm-plex font-medium">
                              {emp.last_name} {emp.first_name}
                              {showAlert && <AlertTriangle className="w-3 h-3 text-amber-500 inline ml-2" />}
                            </TableCell>
                            <TableCell className="font-ibm-plex-mono text-right">
                              {formatCurrency(payroll?.net_pay || 0)}
                            </TableCell>
                            <TableCell className="font-ibm-plex-mono text-right text-red-600">
                              {formatCurrency(payroll?.advances_paid || 0)}
                            </TableCell>
                            <TableCell className="font-ibm-plex-mono text-right font-bold">
                              {formatCurrency(payroll?.total || 0)}
                            </TableCell>
                            <TableCell className="text-center">
                              {isPaid ? (
                                <Check className="w-4 h-4 text-green-600 mx-auto" />
                              ) : (
                                <span className="text-xs text-[#71717A]">-</span>
                              )}
                            </TableCell>
                            <TableCell className="font-ibm-plex text-sm text-[#52525B] max-w-[150px] truncate">
                              {payroll?.notes || "-"}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditPayroll(emp.id)}
                                data-testid={`ghost-edit-payroll-${emp.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Payroll Edit Dialog */}
      <Dialog open={!!editingPayroll} onOpenChange={(open) => { if (!open) setEditingPayroll(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Modifica Stipendio Ghost
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-[#52525B] font-ibm-plex">
              {ghostEmployees.find(e => e.id === editingPayroll)?.last_name} {ghostEmployees.find(e => e.id === editingPayroll)?.first_name}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-ibm-plex">Paga Netta (€)</Label>
                <Input type="number" step="0.01" value={payrollForm.net_pay || ""} onChange={(e) => setPayrollForm({ ...payrollForm, net_pay: e.target.value })} className="font-ibm-plex" data-testid="ghost-payroll-net" />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex">Acconti Versati (€)</Label>
                <Input type="number" step="0.01" value={payrollForm.advances_paid || ""} onChange={(e) => setPayrollForm({ ...payrollForm, advances_paid: e.target.value })} className="font-ibm-plex" data-testid="ghost-payroll-advances" />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex">Ferie (gg)</Label>
                <Input type="number" value={payrollForm.holiday_days_to_deduct || ""} onChange={(e) => setPayrollForm({ ...payrollForm, holiday_days_to_deduct: e.target.value })} className="font-ibm-plex" />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex">ROL (ore)</Label>
                <Input type="number" value={payrollForm.rol_hours_to_deduct || ""} onChange={(e) => setPayrollForm({ ...payrollForm, rol_hours_to_deduct: e.target.value })} className="font-ibm-plex" />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex">Malattia (gg)</Label>
                <Input type="number" value={payrollForm.sick_days || ""} onChange={(e) => setPayrollForm({ ...payrollForm, sick_days: e.target.value })} className="font-ibm-plex" />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex">Rimborso Km (€)</Label>
                <Input type="number" step="0.01" value={payrollForm.km_reimbursement || ""} onChange={(e) => setPayrollForm({ ...payrollForm, km_reimbursement: e.target.value })} className="font-ibm-plex" />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex">Rimborso Spese (€)</Label>
                <Input type="number" step="0.01" value={payrollForm.expense_reimbursement || ""} onChange={(e) => setPayrollForm({ ...payrollForm, expense_reimbursement: e.target.value })} className="font-ibm-plex" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Checkbox id="ghost-paid" checked={payrollForm.is_paid || false} onCheckedChange={(c) => setPayrollForm({ ...payrollForm, is_paid: !!c })} />
                <Label htmlFor="ghost-paid" className="font-ibm-plex cursor-pointer">Pagato</Label>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Note</Label>
              <Textarea value={payrollForm.notes || ""} onChange={(e) => setPayrollForm({ ...payrollForm, notes: e.target.value })} className="font-ibm-plex" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPayroll(null)} className="font-ibm-plex">Annulla</Button>
            <Button onClick={handleSavePayroll} className="bg-black hover:bg-gray-800 text-white font-ibm-plex" data-testid="ghost-save-payroll">
              Salva Stipendio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
